/**
 * AUTHENTICATION HELPERS
 * 
 * This file contains helper functions for user authentication.
 * These functions wrap Firebase Auth and make it easier to use.
 * 
 * Functions included:
 * - signUpWithEmail: Create a new account
 * - signInWithEmail: Log in existing user
 * - signOutUser: Log out
 * - onAuthChange: Listen for auth state changes
 */

import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  type User as FirebaseUser
} from 'firebase/auth';
import { auth } from './config';

/**
 * Sign up a new user with email and password
 * 
 * @param email - User's email address
 * @param password - User's password (min 8 characters)
 * @returns Promise with the user credential
 * 
 * EXAMPLE USAGE:
 * try {
 *   const userCredential = await signUpWithEmail('user@example.com', 'password123');
 *   console.log('User created:', userCredential.user);
 * } catch (error) {
 *   console.error('Error signing up:', error);
 * }
 */
export const signUpWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error: any) {
    // Handle specific Firebase errors
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password should be at least 8 characters');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    throw error;
  }
};

/**
 * Sign in an existing user with email and password
 * 
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise with the user credential
 * 
 * EXAMPLE USAGE:
 * try {
 *   const userCredential = await signInWithEmail('user@example.com', 'password123');
 *   console.log('User signed in:', userCredential.user);
 * } catch (error) {
 *   console.error('Error signing in:', error);
 * }
 */
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error: any) {
    // Handle specific Firebase errors
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email');
    } else if (error.code === 'auth/wrong-password') {
      throw new Error('Incorrect password');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    throw error;
  }
};

/**
 * Sign out the current user
 * 
 * EXAMPLE USAGE:
 * try {
 *   await signOutUser();
 *   console.log('User signed out');
 * } catch (error) {
 *   console.error('Error signing out:', error);
 * }
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Listen for changes in authentication state
 * 
 * This function calls your callback whenever the user logs in or out.
 * 
 * @param callback - Function to call when auth state changes
 * @returns Unsubscribe function (call this to stop listening)
 * 
 * EXAMPLE USAGE:
 * const unsubscribe = onAuthChange((user) => {
 *   if (user) {
 *     console.log('User is signed in:', user.email);
 *   } else {
 *     console.log('User is signed out');
 *   }
 * });
 * 
 * // Later, when component unmounts:
 * unsubscribe();
 */
export const onAuthChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get the current user
 * 
 * @returns The current user or null if not logged in
 * 
 * EXAMPLE USAGE:
 * const user = getCurrentUser();
 * if (user) {
 *   console.log('Current user:', user.email);
 * } else {
 *   console.log('No user logged in');
 * }
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Send email verification to the current user
 * 
 * Call this after sign up to send a verification email.
 * 
 * @returns Promise that resolves when email is sent
 * 
 * EXAMPLE USAGE:
 * try {
 *   await sendVerificationEmail();
 *   console.log('Verification email sent!');
 * } catch (error) {
 *   console.error('Error sending verification email:', error);
 * }
 */
export const sendVerificationEmail = async () => {
  const user = auth.currentUser;
  console.log('[sendVerificationEmail] Current user:', user?.email, 'UID:', user?.uid);

  if (!user) {
    console.error('[sendVerificationEmail] No user logged in');
    throw new Error('No user logged in');
  }

  try {
    console.log('[sendVerificationEmail] Sending verification email to:', user.email);
    await sendEmailVerification(user);
    console.log('[sendVerificationEmail] Verification email sent successfully');
  } catch (error: any) {
    console.error('[sendVerificationEmail] Error:', error.code, error.message);
    if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many requests. Please wait a few minutes before trying again.');
    }
    throw error;
  }
};

/**
 * Send password reset email
 * 
 * @param email - The email address to send the reset link to
 * @returns Promise that resolves when email is sent
 * 
 * EXAMPLE USAGE:
 * try {
 *   await sendPasswordReset('user@example.com');
 *   console.log('Password reset email sent!');
 * } catch (error) {
 *   console.error('Error sending reset email:', error);
 * }
 */
export const sendPasswordReset = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email address');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many requests. Please wait a few minutes before trying again.');
    }
    throw error;
  }
};
